import * as React from 'react';

import {isOptionGroup} from '../../../hooks';

import {SearchableListItemWrapper} from '../SearchableListItem/SearchableListItem';
import {useSearchableListBox} from '../useSearchableListBox';

import {
  SearchableListBoxView,
  SearchableListItemGroupView,
} from './SearchableListBox.styles';
import type {SearchableListBoxProps, SearchableListGroupProps} from './_types';
import SearchableListEmptyState from './SearchableListEmptyState';
import {SearchableListLabel} from './SearchableListBoxLabel';

export const NonVirtualizedSearchableListGroup = ({
  density,
  label,
  options,
  renderOption,
  shouldHideLabel,
  verticalItemAlignment,
}: SearchableListGroupProps) => {
  const groupLabelId = React.useId();

  return (
    <SearchableListItemGroupView aria-labelledby={groupLabelId} role="group">
      <SearchableListLabel id={groupLabelId} isVisuallyHidden={shouldHideLabel}>
        {label}
      </SearchableListLabel>
      {options.map((option) => (
        <SearchableListItemWrapper
          categoryLabel={label}
          density={density}
          key={option.value}
          option={option}
          renderOption={renderOption}
          verticalItemAlignment={verticalItemAlignment}
        />
      ))}
    </SearchableListItemGroupView>
  );
};

export const NonVirtualizedSearchableListBox = React.forwardRef<
  HTMLDivElement,
  SearchableListBoxProps
>(
  (
    {
      density = 'compact',
      hasGroupDividers = false,
      maxHeight,
      verticalItemAlignment = 'top',
      ...props
    }: SearchableListBoxProps,
    ref
  ) => {
    const [isAtScrollTop, setIsAtScrollTop] = React.useState(true);

    const handleScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        setIsAtScrollTop(event.currentTarget.scrollTop === 0);
      },
      []
    );

    const {mergedProps, options, renderOption} = useSearchableListBox({
      ref,
      ...props,
    });

    return (
      <SearchableListBoxView
        hasGroupDividers={hasGroupDividers}
        maxHeight={maxHeight}
        onScroll={handleScroll}
        shouldRenderTopShadow={!isAtScrollTop}
        {...mergedProps}
      >
        {options.length ? (
          options.map((option) => {
            if (isOptionGroup(option)) {
              const {
                options: groupedOptions,
                label,
                shouldHideGroupLabel,
              } = option;

              if (!groupedOptions.length) {
                return null;
              }
              return (
                <NonVirtualizedSearchableListGroup
                  density={density}
                  key={label}
                  label={label}
                  options={groupedOptions}
                  renderOption={renderOption}
                  shouldHideLabel={shouldHideGroupLabel}
                  verticalItemAlignment={verticalItemAlignment}
                />
              );
            }
            return (
              <SearchableListItemWrapper
                density={density}
                key={option.value}
                option={option}
                renderOption={renderOption}
                verticalItemAlignment={verticalItemAlignment}
              />
            );
          })
        ) : (
          <SearchableListEmptyState density={density} />
        )}
      </SearchableListBoxView>
    );
  }
);

NonVirtualizedSearchableListBox.displayName = 'NonVirtualizedSearchableListBox';

export default NonVirtualizedSearchableListBox;
